/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import type { CartItem } from "@farmlands/commerce";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import { isFarmlandsProApiAccountNumberApplied } from "../../../services/farmlandspro-api";
import { useBranch } from "../../../util/useBranch";
import { selectCurrentFarmerAccount } from "../../azure-b2c/storage";
import type { CartItem as LegacyCartItem } from "../../cart/storage/cart.types";
import { getStockAvailabilities, StockAvailabilityType } from "../../products/storage";
import { getProductDetailsByMasterProductIds, TypesenseProductDetails } from "../../typesense/api";

interface CartItemDisplayData {
  details: TypesenseProductDetails | undefined;
  stockAvailabilities: StockAvailabilityType[];
}

export const cartItemDisplayDataQueryKey = (
  accountNumber: string | undefined,
  branchId: string | undefined,
  productIds: number[]
) => ["cart-item-display-data", accountNumber, branchId, ...productIds] as const;

export const useCartItemDisplayData = (items: CartItem[]) => {
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const branchId = useBranch();

  const accountNumber: string | undefined = currentAccount?.accountNumber;
  const productIds: number[] = items.map((item) => item.productId);

  const { data } = useQuery({
    queryKey: cartItemDisplayDataQueryKey(accountNumber, branchId, productIds),
    queryFn: async () => {
      const [stockAvailabilities, detailsByProductId] = await Promise.all([
        getStockAvailabilities({ productIds }),
        getProductDetailsByMasterProductIds(productIds),
      ]);

      return { stockAvailabilities: stockAvailabilities ?? [], detailsByProductId };
    },
    enabled: isFarmlandsProApiAccountNumberApplied(accountNumber) && productIds.length > 0,
  });

  const getDisplayData = (item: CartItem): CartItemDisplayData | undefined => {
    if (!data) {
      return undefined;
    }

    return {
      details: data.detailsByProductId[item.productId],
      stockAvailabilities: data.stockAvailabilities.filter(
        (availability) => availability.productId === item.productId
      ),
    };
  };

  const getDisplayItem = (item: CartItem): LegacyCartItem => {
    const displayData = getDisplayData(item);

    return {
      ...item,
      lifeCycleState: displayData?.details?.lifecycle ?? null,
      stockAvailability: displayData?.details?.stock,
      stockAvailabilities: displayData?.stockAvailabilities ?? [],
      promotions: displayData?.details?.promotions,
      priceBreaks: displayData?.details?.priceBreaks,
    } as LegacyCartItem;
  };

  return { getDisplayItem };
};
